import { Fragment, ReactElement, useState } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import CancelPaymentMethod from './Components/CancelPaymentMethod';
import { MandateDetails, CardDetails } from './Components/Details';
import { ErrorDetails } from './Components/ErrorDetails';
import SenderDetailsModal from './Components/Sender';
import UpdatePaymentUser from './Components/UpdatePaymentUser';
import { TableRowLoader } from '../../components/TableRowLoader';
import { EmbeddedFvError, PaymentMethod, PaymentMethodWithJoinedData } from '../../lib/interface';
import { getCurrencyView, getLocalTimezoneCode, utcDateTimeToLocal } from '../../utils/format';

const getPaymentMethodStatus = (pm: PaymentMethod): string => {
  if (pm.payment_method_type === 'MANDATE') {
    return pm.mandate.status;
  }

  if (pm.payment_method_type === 'CARD') {
    return pm.card.status;
  }

  return 'UNKNOWN';
};

const getPaymentMethodUpdatedAt = (pm: PaymentMethod): string | undefined => {
  if (pm.payment_method_type === 'MANDATE') {
    return pm.mandate.updated_at;
  }

  if (pm.payment_method_type === 'CARD') {
    return pm.card.updated_at;
  }

  return undefined;
};

const getPaymentMethodError = (pm: PaymentMethod): EmbeddedFvError | undefined => {
  if (pm.payment_method_type === 'MANDATE') {
    return pm.mandate.error;
  }

  if (pm.payment_method_type === 'CARD') {
    return pm.card.error;
  }

  return undefined;
};

const isPaymentMethodCancellable = (pm: PaymentMethod): boolean =>
  !['PROCESSING', 'SUBMITTED', 'CREATED', 'CANCELLED'].includes(getPaymentMethodStatus(pm));

enum Dialog {
  None = '',
  Sender = 'SENDER',
  Details = 'DETAILS',
  UpdateUser = 'UPDATE_USER',
  CancelPaymentMethod = 'CANCEL_PAYMENT_METHOD',
  Error = 'ERROR',
}

type PaymentMethodsTableProps = {
  items: PaymentMethodWithJoinedData[];
  isLoading: boolean;
};

export const PaymentMethodsTable = ({ items, isLoading }: PaymentMethodsTableProps): ReactElement => {
  const [openModalId, setOpenModalId] = useState('');
  const [modalType, setModalType] = useState(Dialog.None);

  const localTimezoneCode = getLocalTimezoneCode();

  const openModal = (paymentMethodId: string, modalType: Dialog) => {
    setOpenModalId(paymentMethodId);
    setModalType(modalType);
  };

  const handleModalClose = () => {
    setOpenModalId('');
    setModalType(Dialog.None);
  };

  const handleModalWithRefresh = () => {
    handleModalClose();
    window.location.reload(); // refresh page
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // anchor for the menu
  const [menuId, setMenuId] = useState<null | number>(null); // to know which menu to show

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setMenuId(id); // sets which menu to show
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuId(null);
  };

  const handleShowDropDownMenu = (paymentMethodId: string, modalType: Dialog) => {
    // opens the modal but also closes the menu
    openModal(paymentMethodId, modalType);
    handleMenuClose();
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="payment methods table">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600 } }}>
            <TableCell align="center">Updated At ({localTimezoneCode})</TableCell>
            <TableCell align="center">Payment Method ID</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Type</TableCell>
            <TableCell align="center">Payer</TableCell>
            <TableCell align="center">External User ID</TableCell>
            <TableCell align="center">Details</TableCell>
            <TableCell align="center">Autopay Consent</TableCell>
            <TableCell align="center"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRowLoader rows={10} columns={9} />
          ) : (
            items.map((i, index) => (
              <Fragment key={i.payment_method.payment_method_id}>
                <UpdatePaymentUser
                  title="Edit autopay consent"
                  open={openModalId === i.payment_method.payment_method_id && modalType === Dialog.UpdateUser}
                  handleClose={() => handleModalClose()}
                  handleCloseWithRefresh={() => handleModalWithRefresh()}
                  paymentUser={{
                    senderName: i.payment_user.name,
                    externalUserId: i.payment_user.external_user_id,
                    paymentUserId: i.payment_user.payment_user_id,
                    customerAppId: i.payment_user.customer_app_id,
                    autopayConsent: i.payment_user.autopay_consent,
                  }}
                />
                <CancelPaymentMethod
                  open={openModalId === i.payment_method.payment_method_id && modalType === Dialog.CancelPaymentMethod}
                  handleClose={() => handleModalClose()}
                  handleCloseWithRefresh={() => handleModalWithRefresh()}
                  paymentMethod={{
                    payerName: i.payment_user.name,
                    paymentMethodId: i.payment_method.payment_method_id,
                    paymentMethodData: i.payment_method,
                    customerAppId: i.payment_user.customer_app_id,
                    status: getPaymentMethodStatus(i.payment_method),
                  }}
                />
                <SenderDetailsModal
                  title="Payment Method Sender"
                  open={openModalId === i.payment_method.payment_method_id && modalType === Dialog.Sender}
                  handleClose={() => handleModalClose()}
                  sender={{
                    name: i.payment_user.name,
                    fvUserId: i.payment_user.payment_user_id,
                    externalUserId: i.payment_user.external_user_id,
                    senderType: i.payment_user.payment_user_type,
                    // no sender institution
                  }}
                />
                {i.payment_method.payment_method_type === 'MANDATE' && (
                  <MandateDetails
                    open={openModalId === i.payment_method.payment_method_id && modalType === Dialog.Details}
                    handleClose={() => handleModalClose()}
                    title="Mandate Details"
                    details={{
                      payerName: i.payment_user.name,
                      payerType: i.payment_user.payment_user_type,
                      institutionName: i.payment_method.mandate.sender_account?.institution_name,
                      institutionId: i.payment_method.mandate.sender_account?.institution_id,
                      accountNumber: i.payment_method.mandate.sender_account?.account_number_masked,
                      currency: i.payment_method.mandate.mandate_details.currency,
                      description: i.payment_method.mandate.mandate_details.description,
                      startDate: i.payment_method.mandate.mandate_details.start_date,
                      endDate: i.payment_method.mandate.mandate_details.end_date,
                      maxTxnAmount: getCurrencyView(
                        i.payment_method.mandate.mandate_details.currency,
                        i.payment_method.mandate.mandate_details.transaction_limits?.max_transaction_amount,
                      ),
                      maxTxnAmountSetByPayerInitial: getCurrencyView(
                        i.payment_method.mandate.mandate_details.currency,
                        i.payment_method.mandate.mandate_details.transaction_limits
                          ?.max_transaction_amount_set_by_payer_initial,
                      ),
                      maxTotalAmount: getCurrencyView(
                        i.payment_method.mandate.mandate_details.currency,
                        i.payment_method.mandate.mandate_details.transaction_limits?.max_period_amount,
                      ),
                      maxTxnCount: i.payment_method.mandate.mandate_details.transaction_limits?.max_period_count,
                      period: i.payment_method.mandate.mandate_details.transaction_limits?.period ?? '',
                      collectionEntityName: i.payment_method.mandate.mandate_details.collection_entity_name,
                      processorEntityName: i.payment_method.mandate.mandate_details.processor_entity_name,
                      recipientName: i.payment_method.mandate.recipient.name,
                      bankReference: i.payment_method.mandate.mandate_details.mandate_bank_reference,
                      ddaReference: i.payment_method.mandate.mandate_details.dda_reference,
                      upiId: i.payment_method.mandate.mandate_details.upi_id,
                      customerAppId: i.payment_user.customer_app_id,
                      externalUserId: i.payment_user.external_user_id,
                      finverseUserId: i.payment_user.payment_user_id,
                      finverseAuthorizationReference:
                        i.payment_method.mandate.mandate_details.finverse_authorization_reference,
                      mandateId: i.payment_method.mandate.mandate_id,
                      paymentMethodId: i.payment_method.payment_method_id,
                      recipientAccountId: i.payment_method.mandate.recipient.recipient_account_id,
                      senderAccountId: i.payment_method.mandate.sender_account?.account_id,
                      settlementAccountReference:
                        i.payment_method.mandate.recipient_account?.settlement_account_reference,
                      businessUnits: i.payment_method.mandate.recipient_account?.business_units,
                      createdAt: i.payment_method.created_at,
                      updatedAt: i.payment_method.mandate.updated_at,
                      live: i.payment_method.live,
                      metadata: i.payment_method.mandate.metadata,
                      integration_metadata: i.payment_method.integration_metadata,
                    }}
                  />
                )}
                {i.payment_method.payment_method_type === 'CARD' && (
                  <CardDetails
                    open={openModalId === i.payment_method.payment_method_id && modalType === Dialog.Details}
                    handleClose={() => handleModalClose()}
                    title="Card Details"
                    details={{
                      payerName: i.payment_user.name,
                      cardholderName: i.payment_method.card.card_details?.accountholder_name,
                      cardBrand: i.payment_method.card.card_details?.brand ?? '',
                      cardLast4: i.payment_method.card.card_details?.last4 ?? '',
                      cardExpiryMonth: i.payment_method.card.card_details?.expiry_month?.toString(),
                      cardExpiryYear: i.payment_method.card.card_details?.expiry_year?.toString(),
                      cardCountry: i.payment_method.card.card_details?.country,
                      collectionEntityName: i.payment_method.card.card_details?.collection_entity_name,
                      recipientName: i.payment_method.card.recipient?.name,
                      acquirerAuthorizationReference:
                        i.payment_method.card.card_details?.acquirer_authorization_reference,
                      finverseAuthorizationReference:
                        i.payment_method.card.card_details?.finverse_authorization_reference,
                      processorId: i.payment_method.card.card_details?.processor_details?.processor_id,
                      processorReference: i.payment_method.card.card_details?.processor_details?.processor_reference,
                      paymentMethodId: i.payment_method.payment_method_id,
                      customerAppId: i.payment_user.customer_app_id,
                      externalUserId: i.payment_user.external_user_id,
                      finverseUserId: i.payment_user.payment_user_id,
                      recipientAccountId: i.payment_method.card.recipient_account?.account_id,
                      settlementAccountReference: i.payment_method.card.recipient_account?.settlement_account_reference,
                      businessUnits: i.payment_method.card.recipient_account?.business_units,
                      createdAt: i.payment_method.created_at,
                      updatedAt: i.payment_method.card.updated_at,
                      metadata: i.payment_method.card.metadata,
                      integration_metadata: i.payment_method.integration_metadata,
                    }}
                  />
                )}
                {getPaymentMethodStatus(i.payment_method) === 'FAILED' && (
                  <ErrorDetails
                    open={i.payment_method.payment_method_id === openModalId && modalType === Dialog.Error}
                    handleClose={handleModalClose}
                    error={getPaymentMethodError(i.payment_method)}
                  />
                )}
                <TableRow>
                  <TableCell align="center">
                    {getPaymentMethodUpdatedAt(i.payment_method)
                      ? utcDateTimeToLocal(getPaymentMethodUpdatedAt(i.payment_method) as string)
                      : ''}
                  </TableCell>
                  <TableCell align="center">{i.payment_method.payment_method_id}</TableCell>
                  <TableCell align="center">
                    {getPaymentMethodStatus(i.payment_method) !== 'FAILED' ? (
                      getPaymentMethodStatus(i.payment_method)
                    ) : (
                      <Button
                        size="large"
                        sx={{ padding: 0 }}
                        onClick={() => {
                          setModalType(Dialog.Error);
                          setOpenModalId(i.payment_method.payment_method_id);
                        }}
                      >
                        {getPaymentMethodStatus(i.payment_method)}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">{i.payment_method.payment_method_type}</TableCell>
                  <TableCell align="center">
                    <Button onClick={() => openModal(i.payment_method.payment_method_id, Dialog.Sender)}>
                      {i.payment_user.name}
                    </Button>
                  </TableCell>
                  <TableCell align="center">{i.payment_user.external_user_id}</TableCell>
                  <TableCell align="center">
                    <Button onClick={() => openModal(i.payment_method.payment_method_id, Dialog.Details)}>
                      Details
                    </Button>
                  </TableCell>
                  <TableCell align="center">{i.payment_user.autopay_consent ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="center">
                    <IconButton aria-label="more" onClick={(e) => handleMenuClick(e, index)}>
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu // since we add each for each row, we will use menuId to know which menu to show
                      id="basic-menu"
                      open={index === menuId} // to know which menu to show
                      anchorEl={anchorEl}
                      onClose={handleMenuClose}
                      MenuListProps={{
                        'aria-labelledby': 'basic-button',
                      }}
                    >
                      <MenuItem
                        onClick={() => handleShowDropDownMenu(i.payment_method.payment_method_id, Dialog.UpdateUser)}
                      >
                        Edit Autopay Consent
                      </MenuItem>
                      <Tooltip
                        title={
                          isPaymentMethodCancellable(i.payment_method)
                            ? ''
                            : "Payment methods that are processing, not yet set up, or already cancelled can't be cancelled"
                        }
                        placement="left"
                      >
                        <span style={{ display: 'block' }}>
                          <MenuItem
                            disabled={!isPaymentMethodCancellable(i.payment_method)}
                            sx={isPaymentMethodCancellable(i.payment_method) ? { color: 'error.main' } : undefined}
                            onClick={() =>
                              handleShowDropDownMenu(i.payment_method.payment_method_id, Dialog.CancelPaymentMethod)
                            }
                          >
                            Cancel Payment Method
                          </MenuItem>
                        </span>
                      </Tooltip>
                    </Menu>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
